<template>
  <div class="billing-operation-mode">
    <operation-context-alert>
      {{ relationContextText }}
    </operation-context-alert>

    <operation-summary-strip :items="summaryItems" />

    <operation-panel
      :title="relationPanelTitle"
      :subtitle="relationPanelSubtitle"
      tone="primary"
      :chip-label="relationPanelChipLabel"
      chip-color="primary"
    >
      <div class="operation-field-grid operation-field-grid--three">
        <div class="operation-field">
          <div class="operation-field__label">Müşteri</div>
          <div class="operation-field__readonly">
            <span>{{ customerName }}</span>
            <v-icon small color="#757575">mdi-menu-down</v-icon>
          </div>
          <div class="operation-field__hint">Güncel fatura sahibi</div>
        </div>
        <div class="operation-field">
          <div class="operation-field__label">
            {{ hasRelationHistory ? "Yeni başlangıç" : "İlişki başlangıcı" }}
          </div>
          <rs-datepicker
            class="operation-field-input operation-field-input--compact"
            :value="relation.starts_on"
            label=""
            :rules="[rules.required]"
            hide-details
            @input="updateRelationField('starts_on', $event)"
          />
          <div class="operation-field__hint">{{ relationStartsOnHint }}</div>
        </div>
        <div class="operation-field">
          <div class="operation-field__label">Bitiş</div>
          <rs-datepicker
            class="operation-field-input operation-field-input--compact"
            :value="relation.ends_on"
            label=""
            :disabled="!relation.starts_on"
            :min="relation.starts_on"
            hide-details
            @input="updateRelationField('ends_on', $event)"
          />
          <div class="operation-field__hint">
            {{ hasRelationHistory ? "Aktif kalır" : "Boş bırakılabilir" }}
          </div>
        </div>
      </div>
    </operation-panel>

    <operation-blocked-reasons
      v-if="visibleBlockedReasons.length"
      :reasons="visibleBlockedReasons"
    />
  </div>
</template>

<script>
import validation from "@/core/validation";
import OperationBlockedReasons from "../shared/OperationBlockedReasons.vue";
import OperationContextAlert from "../shared/OperationContextAlert.vue";
import OperationPanel from "../shared/OperationPanel.vue";
import OperationSummaryStrip from "../shared/OperationSummaryStrip.vue";
import {
  formatDate,
  formatDateRange,
  getInvoiceSummaryLabel,
} from "./modeFormatters";

export default {
  name: "RelationOperationMode",
  components: {
    OperationBlockedReasons,
    OperationContextAlert,
    OperationPanel,
    OperationSummaryStrip,
  },
  props: {
    draft: {
      type: Object,
      required: true,
    },
    preview: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      rules: validation,
    };
  },
  computed: {
    relation() {
      return this.draft.relation || {};
    },
    customerName() {
      return this.preview?.customer?.name || "Seçilen müşteri";
    },
    clusterName() {
      return this.preview?.cluster?.name || "Seçilen TYA";
    },
    relations() {
      return this.preview?.relations || [];
    },
    customerProducts() {
      return this.preview?.customer_products || this.preview?.products || [];
    },
    blockedReasons() {
      return this.preview?.resolution_plan?.blocked_reasons || [];
    },
    visibleBlockedReasons() {
      return this.blockedReasons.filter(
        (reason) => !String(reason || "").startsWith("Ürün:")
      );
    },
    hasRelationHistory() {
      return this.relations.length > 0;
    },
    riskType() {
      return (
        this.preview?.source?.issue_type || this.preview?.risk_type || null
      );
    },
    isMissingRelationOperation() {
      return this.riskType === "missing_relation";
    },
    isEndedRelationOperation() {
      return this.riskType === "ended_relation";
    },
    relationContextText() {
      if (this.isEndedRelationOperation) {
        return "Önceki müşteri-TYA ilişkisi bitmiş. Yeni devam ilişkisi son kapanışın ertesi günüyle başlatılır.";
      }

      if (this.isMissingRelationOperation) {
        if (!this.hasRelationHistory) {
          return "Hiç geçmiş ilişki yok. İlk müşteri-TYA ilişkisi kullanıcı tarafından seçilen tarihle oluşturulur.";
        }

        return "Mevcut ilişki geçmişi bulundu. Yeni başlangıç tarihi faturalı dönemler ve mevcut ilişki sınırlarıyla kontrol edilir.";
      }

      if (!this.hasRelationHistory) {
        return "Hiç geçmiş ilişki yok. İlk ilişki tarihi kullanıcı tarafından seçilir.";
      }

      return "Mevcut ilişki geçmişi bulundu. Yeni başlangıç tarihi faturalı dönemler ve mevcut ilişki sınırlarıyla kontrol edilir.";
    },
    latestRelation() {
      return this.relations[0] || null;
    },
    relationPanelTitle() {
      if (this.isEndedRelationOperation) {
        return "Müşteri ilişkisini devam ettir";
      }

      if (this.isMissingRelationOperation && !this.hasRelationHistory) {
        return "Müşteri ilişkisi ekle";
      }

      return this.hasRelationHistory
        ? "Müşteri ilişkisini devam ettir"
        : "Müşteri ilişkisi ekle";
    },
    relationPanelSubtitle() {
      if (this.isEndedRelationOperation) {
        return "Yeni ilişki, son kapanışın ertesi günüyle başlar.";
      }

      if (this.isMissingRelationOperation) {
        return this.hasRelationHistory
          ? "Yeni ilişki, son kapanışın ertesi günüyle başlar."
          : "Bu işlem yalnızca müşteri-TYA ilişkisini tamamlar.";
      }

      return this.hasRelationHistory
        ? "Yeni ilişki, son kapanışın ertesi günüyle başlar."
        : "Ürün adımı, bu ilişki kontrol edilmeden açılmaz.";
    },
    relationPanelChipLabel() {
      return this.hasRelationHistory ? "Devam" : "Adım 1";
    },
    relationStartsOnHint() {
      return this.hasRelationHistory
        ? "Son ilişki bitişi + 1 gün"
        : "Kullanıcı seçimi";
    },
    summaryItems() {
      if (this.hasRelationHistory) {
        return [
          {
            label: "Son ilişki",
            value: formatDateRange(
              this.latestRelation?.starts_on,
              this.latestRelation?.ends_on
            ),
          },
          {
            label: "Önerilen başlangıç",
            value: this.relation.starts_on
              ? formatDate(this.relation.starts_on)
              : "-",
          },
          {
            label: "Faturalı dönem",
            value: getInvoiceSummaryLabel(this.latestRelation?.invoice_summary),
          },
          {
            label: "Ürün",
            value:
              this.customerProducts.length > 0
                ? `${this.customerProducts.length} kayıt`
                : "Yok",
          },
        ];
      }

      return [
        {
          label: "Fatura sahibi",
          value: this.customerName,
        },
        {
          label: "TYA",
          value: this.clusterName,
        },
        {
          label: "Geçmiş ilişki",
          value:
            this.relations.length > 0
              ? `${this.relations.length} kayıt`
              : "Yok",
        },
        {
          label: "Aktif ürün",
          value:
            this.customerProducts.length > 0
              ? `${this.customerProducts.length} kayıt`
              : "Yok",
        },
      ];
    },
  },
  methods: {
    updateRelationField(field, value) {
      this.$emit("update-draft", {
        namespace: "relation",
        field,
        value,
      });
    },
  },
};
</script>

<style src="../shared/operation-fields.css"></style>
